/**
 * "I already submitted this universe" — the recipient's statement on a pending
 * universe request (operator order 2026-10-18).
 *
 * Shown inside the Scrubber and Reporter request banners for a signed in,
 * named recipient. The statement pauses the reminders and asks compliance to
 * confirm; it never completes the request. One sentence of context is
 * optional (how and when it went: SFTP, a prior upload, by hand).
 *
 * The same control for compliance (mode 'compliance', operator report
 * 2026-10-26): a compliance user is not a recipient, so the button is their
 * Mark received instead. The server marks the pending universes of this
 * application received on the request; the request completes when nothing
 * else is pending, and the reminders stop naming what is in.
 *
 * POST /api/universe-ack/request/:id/acknowledge — session + organization,
 * recipient or module check on the server. No member data travels; a note only.
 */
import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function UniverseRequestAcknowledge({ request, autoOpen = false, onAcknowledged = null, compact = false, mode = 'recipient', consumerApp = null }) {
  const compliance = mode === 'compliance';
  const [open, setOpen] = useState(!!autoOpen);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(!compliance && request?.acknowledged_at ? { mode: 'recipient', acknowledged_at: request.acknowledged_at, acknowledged_by: request.acknowledged_by } : null);

  if (!request) return null;

  if (done) {
    if (done.mode === 'compliance') {
      return (
        <div className={`${compact ? 'text-xs' : 'text-sm'} text-slate-700 mt-2`}>
          <span className="font-semibold text-emerald-700">Marked received</span>
          {done.received_by ? <> by {done.received_by}</> : null}
          {done.received_at ? <> on {new Date(done.received_at).toLocaleDateString()}</> : null}.{' '}
          {done.request_completed
            ? 'The request is complete and its reminders have stopped.'
            : 'The request stays open for its other items; reminders now name only those.'}
        </div>
      );
    }
    return (
      <div className={`${compact ? 'text-xs' : 'text-sm'} text-slate-700 mt-2`}>
        <span className="font-semibold text-emerald-700">Reported as submitted</span>
        {done.acknowledged_by ? <> by {done.acknowledged_by}</> : null}
        {done.acknowledged_at ? <> on {new Date(done.acknowledged_at).toLocaleDateString()}</> : null}. Reminders are paused until compliance confirms.
      </div>
    );
  }

  async function submit() {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API}/api/universe-ack/request/${encodeURIComponent(request.id)}/acknowledge`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.trim() || null, ...(consumerApp ? { consumer_app: consumerApp } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not record your statement.');
      if (data.mode === 'compliance') {
        setDone({ mode: 'compliance', received_at: data.received_at, received_by: data.received_by, request_completed: !!data.request_completed });
      } else {
        setDone({ mode: 'recipient', acknowledged_at: data.acknowledged_at, acknowledged_by: data.acknowledged_by });
      }
      if (onAcknowledged) onAcknowledged(data);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="mt-2">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}
          className={`${compact ? 'text-[11px]' : 'text-xs'} font-semibold underline text-slate-700 hover:text-slate-900`}>
          {compliance ? 'Already submitted this universe another way? Mark it received.' : 'Already submitted this universe another way? Tell compliance.'}
        </button>
      ) : (
        <div className="border border-slate-200 bg-white rounded-md p-3 max-w-xl">
          <div className="text-xs font-semibold text-slate-800">{compliance ? 'Mark this universe received' : 'Report this universe as already submitted'}</div>
          <div className="text-[11.5px] text-slate-600 mt-0.5">
            {compliance
              ? 'You are answering for compliance: the pending universes on this request are marked received, the request closes when nothing else is pending, and the reminders stop naming them. Say how it arrived if you know (for example SFTP on the 3rd). Do not include member information.'
              : 'Say how it went (for example SFTP on the 3rd, or uploaded last week). Reminders pause and compliance confirms on their side. Do not include member information.'}
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={2} maxLength={500}
            placeholder="Optional: how and when the universe was submitted"
            className="mt-2 w-full border border-slate-300 rounded px-2 py-1.5 text-xs text-slate-900 focus:outline-none focus:border-slate-500" />
          {error && <div className="text-[11.5px] text-red-700 mt-1">{error}</div>}
          <div className="flex items-center gap-2 mt-2">
            <button type="button" disabled={busy} onClick={submit}
              className="px-3 py-1.5 rounded bg-slate-900 text-white text-xs font-semibold disabled:bg-slate-300">
              {busy ? 'Recording…' : 'Yes, it has been submitted'}
            </button>
            <button type="button" disabled={busy} onClick={() => setOpen(false)}
              className="px-2 py-1.5 text-xs text-slate-600 hover:text-slate-900">Not now</button>
          </div>
        </div>
      )}
    </div>
  );
}
